import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Image as ImageIcon,
  FileText as PdfIcon,
  File as FileIcon,
  CheckCircle2 as CheckCircleIcon,
  XCircle as CancelIcon,
  Ban as BlockIcon,
  SkipForward as SkipNextIcon,
  ArrowLeft as ArrowBackIcon,
  ArrowRight as ArrowRightIcon,
  Pencil as EditIcon,
  Eye as VisibilityIcon,
  Paperclip as AttachFileIcon,
  Trash2 as DeleteIcon,
  Download,
  FileImage,
  Settings,
  Bug,
  RotateCcw,
  X,
  ChevronDown,
  ChevronRight,
  Plus,
  Filter,
  Columns,
  GripVertical,
  Search,
  Check,
  Lock,
  Folder,
  FolderOpen,
} from "lucide-react";
import sectionService from "../services/sectionService";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import "../components/testcase/SectionBasedTestCaseView.css";
import { Card } from "../components/ui/card";
import PageSkeleton from "../components/skeletons/PageSkeleton";
import { testrunService } from "../services/testrunService";
import { teststepsService } from "../services/teststepsService";
import s3UploadService from "../services/s3UploadService";
import AddResultModal from "../components/testrun/AddResultModal";
import AssignToModal from "../components/testrun/AssignToModal";
import LinkDefectModal from "../components/defects/LinkDefectModal";
import PushNewDefectModal from "../components/defects/PushNewDefectModal";
import { useAuth } from "../context/AuthContext";
import { getStoredClientId } from "../utils/tokenUtils";
import { formatDate, formatDateTime } from "../utils/formatters";
import toast from "react-hot-toast";

const NoContentComponent = ({ title }) => {
  return (
    <div className="flex flex-col items-center justify-center">
      <img
        width={100}
        height={100}
        className="block h-[100px] w-[100px] object-contain"
        src={require("../assets/NoContent.png")}
        alt="Empty State"
      />
      <span className="text-sm text-gray-600">{title}</span>
    </div>
  );
};

/** Slightly lighten a hex color for gradient stops (simple blend toward white) */
const lightenHex = (hex, amount = 0.35) => {
  const h = hex.replace("#", "");
  if (h.length !== 6) return hex;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const mix = (c) =>
    Math.round(c + (255 - c) * amount)
      .toString(16)
      .padStart(2, "0");
  return `#${mix(r)}${mix(g)}${mix(b)}`;
};

const TestRunStatusPieTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const item = payload[0];
  const src = item.payload ?? item;
  const name = src.name ?? item.name;
  const value = src.value ?? item.value;
  const color = src.color ?? item.color ?? item.fill;
  if (value == null) return null;
  return (
    <div
      className="rounded-xl border border-stone-200/90 bg-white/95 px-3.5 py-2.5 text-sm shadow-[0_10px_40px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.06)] backdrop-blur-md"
      style={{
        boxShadow:
          "0 4px 20px rgba(0,0,0,0.1), 0 0 0 1px rgba(255,255,255,0.6) inset",
      }}
    >
      <div className="flex items-center gap-2.5">
        <span
          className="h-2.5 w-2.5 shrink-0 rounded-full shadow-sm ring-1 ring-white/50"
          style={{ backgroundColor: color }}
        />
        <div>
          <p className="font-semibold leading-tight text-stone-900">{name}</p>
          <p className="mt-0.5 text-xs text-stone-500">
            <span className="font-medium tabular-nums text-stone-700">
              {value}
            </span>{" "}
            {value === 1 ? "case" : "cases"}
          </p>
        </div>
      </div>
    </div>
  );
};

const TestRunLabelBadge = ({ label }) => (
  <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700 mr-1">
    {label}
  </span>
);

const EXECUTION_SELECT_STATUSES = [
  "pending",
  "passed",
  "failed",
  "blocked",
  "skipped",
];

function normalizeExecutionStatusForSelect(status) {
  if (!status || status === "untested") return "pending";
  return EXECUTION_SELECT_STATUSES.includes(status) ? status : "pending";
}

const triggerClassFor = (s) => {
  const baseClass =
    "h-8 w-[6.5rem] max-w-[6.5rem] shrink-0 p-1.5 text-xs rounded-full flex flex-row items-center justify-center gap-0.5 border-0 shadow-none [&>span]:truncate [&>span]:min-w-0";
  switch (s) {
    case "pending":
      return `${baseClass} bg-[#645E5BFF] text-white`;
    case "passed":
      return `${baseClass} bg-[#1DD75BFF] text-white`;
    case "failed":
      return `${baseClass} bg-[#DE3B40FF] text-white`;
    case "blocked":
      return `${baseClass} bg-[#24211FFF] text-white`;
    case "skipped":
      return `${baseClass} bg-[#EFB034FF] text-white`;
    default:
      return `${baseClass} bg-gray-100 text-gray-800`;
  }
};

/**
 * Sync function component only. Async components are invalid in React (they return a Promise).
 * Controlled from `status` so parent state stays source of truth after API updates.
 */
const ExecutionStatusDropdown = ({
  status,
  testRunCaseId,
  onStatusChange,
  disabled = false,
}) => {
  const value = normalizeExecutionStatusForSelect(status);

  return (
    <Select
      value={value}
      onValueChange={(newValue) => onStatusChange(testRunCaseId, newValue)}
      disabled={disabled}
    >
      <SelectTrigger className={triggerClassFor(value)}>
        <SelectValue placeholder="Select status" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="pending">Pending</SelectItem>
        <SelectItem value="passed">Passed</SelectItem>
        <SelectItem value="failed">Failed</SelectItem>
        <SelectItem value="blocked">Blocked</SelectItem>
        <SelectItem value="skipped">Skipped</SelectItem>
      </SelectContent>
    </Select>
  );
};

const TestRunExecutionPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const testRunId = searchParams.get("test_run_id");
  const projectId = searchParams.get("project_id");
  const projectName = searchParams.get("project_name") || "Project";

  const [testRun, setTestRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTestCase, setSelectedTestCase] = useState(null);
  const [executionStatus, setExecutionStatus] = useState("pending");
  const [notes, setNotes] = useState("");
  const [actualHours, setActualHours] = useState("");
  const [selectedCases, setSelectedCases] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState("pending");
  const [testSteps, setTestSteps] = useState([]);
  const [loadingSteps, setLoadingSteps] = useState(false);
  const [resultAttachments, setResultAttachments] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [showAddResultModal, setShowAddResultModal] = useState(false);
  const [addingResultCase, setAddingResultCase] = useState(null);
  const [showAssignToModal, setShowAssignToModal] = useState(false);
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [subgroups, setSubgroups] = useState([]);
  const [activeTab, setActiveTab] = useState("results");
  const [testCaseAttachments, setTestCaseAttachments] = useState([]);
  const [showPushDefectModal, setShowPushDefectModal] = useState(false);
  const [showLinkDefectModal, setShowLinkDefectModal] = useState(false);
  const [linkDefectTargets, setLinkDefectTargets] = useState(null);
  const [sectionLoading, setSectionLoading] = useState(false);
  const [sidebarMode, setSidebarMode] = useState("subgroups");
  const [currentTreeSection, setCurrentTreeSection] = useState(null);
  const [scrollToSectionId, setScrollToSectionId] = useState(null);
  const [tableSearch, setTableSearch] = useState("");
  const [assignees, setAssignees] = useState([]);
  /** Section tree map from last full fetch; reused when grouping silently to avoid getSectionTree. */
  const sectionMapRef = useRef(new Map());
  const { user } = useAuth();
  const clientId =
    searchParams.get("client_id") || getStoredClientId() || user?.client_id;

  useEffect(() => {
    sectionMapRef.current = new Map();
  }, [projectId]);

  const displaySubgroups = useMemo(() => {
    let list = subgroups;
    if (currentTreeSection != null && sidebarMode === "subgroups") {
      list = subgroups.filter(
        (sg) => String(sg.section_id) === String(currentTreeSection),
      );
    }
    if (tableSearch.trim()) {
      const q = tableSearch.toLowerCase();
      list = list
        .map((sg) => ({
          ...sg,
          test_cases: (sg.test_cases || []).filter((tc) =>
            (tc.title || "").toLowerCase().includes(q),
          ),
        }))
        .filter((sg) => (sg.test_cases || []).length > 0);
    }
    return list;
  }, [subgroups, currentTreeSection, sidebarMode, tableSearch]);

  const filteredFallbackTestCases = useMemo(() => {
    if (!testRun?.test_cases) return [];
    if (!tableSearch.trim()) return testRun.test_cases;
    const q = tableSearch.toLowerCase();
    return testRun.test_cases.filter((tc) =>
      (tc.title || "").toLowerCase().includes(q),
    );
  }, [testRun?.test_cases, tableSearch]);

  useEffect(() => {
    if (scrollToSectionId == null) return;
    const t = window.setTimeout(() => {
      const el = document.getElementById(
        `testrun-section-${scrollToSectionId}`,
      );
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      setScrollToSectionId(null);
    }, 50);
    return () => window.clearTimeout(t);
  }, [scrollToSectionId]);

  const groupTestCasesBySection = useCallback(
    async (testCases, silent = false) => {
      if (!silent) {
        setSectionLoading(true);
      }
      const mapFolderToSection = (folder) => {
        const section = {
          ...folder,
          section_id: folder.section_id || folder.folder_id,
          parent_section_id:
            folder.parent_section_id !== undefined
              ? folder.parent_section_id
              : folder.parent_folder_id,
          children: folder.children
            ? folder.children.map(mapFolderToSection)
            : undefined,
        };
        return section;
      };

      try {
        let sectionMap = new Map();
        const canSkipSectionFetch =
          silent && sectionMapRef.current.size > 0;

        if (canSkipSectionFetch) {
          sectionMap = new Map(sectionMapRef.current);
        } else {
          const sectionsResponse =
            await sectionService.getSectionTree(projectId);
          const foldersFromAPI =
            sectionsResponse?.sections ||
            sectionsResponse?.data?.sections ||
            sectionsResponse?.data?.folders ||
            sectionsResponse?.folders ||
            (Array.isArray(sectionsResponse) ? sectionsResponse : []);
          const treeSections = Array.isArray(foldersFromAPI)
            ? foldersFromAPI.map(mapFolderToSection)
            : [];

          sectionMap = new Map();
          const addToMap = (list) => {
            (list || []).forEach((section) => {
              const sectionId = section.section_id || section.folder_id;
              if (sectionId) {
                sectionMap.set(sectionId, section);
              }
              if (section.children && section.children.length) {
                addToMap(section.children);
              }
            });
          };
          addToMap(treeSections);
          sectionMapRef.current = new Map(sectionMap);
        }

        if (!testCases || testCases.length === 0) {
          setSubgroups([]);
          setExpandedSections(new Set());
          return;
        }

        const grouped = {};
        testCases.forEach((testCase) => {
          const sectionId =
            testCase.section_id || testCase.folder_id || "ungrouped";
          if (!grouped[sectionId]) {
            const section = sectionMap.get(sectionId);
            grouped[sectionId] = {
              section_id: sectionId === "ungrouped" ? "ungrouped" : sectionId,
              section_name:
                section?.name ||
                section?.path ||
                section?.folder_name ||
                "Ungrouped",
              test_cases: [],
            };
          }
          grouped[sectionId].test_cases.push(testCase);
        });

        const subgroupsArray = Object.values(grouped).map((group) => {
          const total = group.test_cases.length;
          const completed = group.test_cases.filter(
            (tc) =>
              tc.execution_status &&
              tc.execution_status !== "pending" &&
              tc.execution_status !== "untested",
          ).length;
          const progress =
            total > 0 ? Math.round((completed / total) * 100) : 0;

          return {
            ...group,
            total,
            completed,
            progress,
          };
        });

        setSubgroups(
          subgroupsArray.length > 0
            ? subgroupsArray
            : [
                {
                  section_id: "all",
                  section_name: "All Test Cases",
                  test_cases: testCases,
                  total: testCases.length,
                  completed: testCases.filter(
                    (tc) =>
                      tc.execution_status &&
                      tc.execution_status !== "pending" &&
                      tc.execution_status !== "untested",
                  ).length,
                  progress:
                    testCases.length > 0
                      ? Math.round(
                          (testCases.filter(
                            (tc) =>
                              tc.execution_status &&
                              tc.execution_status !== "pending" &&
                              tc.execution_status !== "untested",
                          ).length /
                            testCases.length) *
                            100,
                        )
                      : 0,
                },
              ],
        );

        const allSectionIds =
          subgroupsArray.length > 0
            ? subgroupsArray.map((g) => g.section_id)
            : ["all"];
        setExpandedSections(new Set(allSectionIds));
      } catch (error) {
        console.error("Error grouping test cases:", error);
        const fallbackGroup = {
          section_id: "all",
          section_name: "All Test Cases",
          test_cases: testCases,
          total: testCases.length,
          completed: testCases.filter(
            (tc) =>
              tc.execution_status &&
              tc.execution_status !== "pending" &&
              tc.execution_status !== "untested",
          ).length,
          progress:
            testCases.length > 0
              ? Math.round(
                  (testCases.filter(
                    (tc) =>
                      tc.execution_status &&
                      tc.execution_status !== "pending" &&
                      tc.execution_status !== "untested",
                  ).length /
                    testCases.length) *
                    100,
                )
              : 0,
        };
        setSubgroups([fallbackGroup]);
        setExpandedSections(new Set(["all"]));
      } finally {
        setSectionLoading(false);
      }
    },
    [projectId],
  );

  const loadTestRun = useCallback(
    async (silent = false) => {
      if (!testRunId || !projectId) return;
      if (!silent) {
        setLoading(true);
      }
      try {
        const response = await testrunService.getTestRun(testRunId, projectId);
        if (response.success) {
          setTestRun(response.data);
          await groupTestCasesBySection(
            response.data?.test_cases || [],
            silent,
          );
          setAssignees(response.data?.assigned_to_values || []);
        }
      } catch (error) {
        console.error("Error loading test run:", error);
        if (!silent) {
          toast.error("Failed to load test run");
        }
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [testRunId, projectId, groupTestCasesBySection],
  );

  const handleAssignSuccess = useCallback(async () => {
    await loadTestRun(true);
    if (!selectedTestCase?.test_run_case_id) return;
    try {
      const details = await testrunService.getTestCaseDetails(
        testRunId,
        selectedTestCase.test_run_case_id,
      );
      if (details?.success) {
        setSelectedTestCase(details.data);
        setNotes(details.data?.notes ?? "");
      }
    } catch (e) {
      console.error("Error refreshing test case after assign:", e);
    }
  }, [loadTestRun, selectedTestCase, testRunId]);

  useEffect(() => {
    loadTestRun();
    const intervalId = setInterval(() => {
      loadTestRun(true);
    }, 30000);
    return () => clearInterval(intervalId);
  }, [loadTestRun]);

  const handleStatusChange = async (testRunCaseId, newStatus) => {
    // Check if test run is completed
    if (testRun?.status === "completed") {
      toast.error("Cannot update status for a completed test run");
      return;
    }

    try {
      await testrunService.updateTestCaseExecution(
        testRunId,
        testRunCaseId,
        projectId,
        {
          execution_status: newStatus,
        },
      );
      toast.success("Status updated successfully");
      // Update local state instead of reloading
      const updatedTestCases = testRun.test_cases.map((tc) =>
        tc.test_run_case_id === testRunCaseId
          ? {
              ...tc,
              execution_status: newStatus,
              executed_at: new Date().toISOString(),
            }
          : tc,
      );
      setTestRun((prev) => ({
        ...prev,
        test_cases: updatedTestCases,
      }));
      // Update subgroups
      await groupTestCasesBySection(updatedTestCases, true);
    } catch (error) {
      console.error("Error updating status:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to update status";
      if (error.response?.data?.code === "TEST_RUN_COMPLETED") {
        toast.error("Cannot update status for a completed test run");
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const handleEdit = (testCase) => {
    handleViewResults(testCase);
    navigate(
      `test-runs/new?project_id=7bfcc486-a3c5-4169-a8c9-d13deb62afb1&project_name=for%20saumya&test_run_id=${testRunId}&mode=edit`,
    );
  };

  const handleViewResults = async (testCase) => {
    // setSelectedTestCase(testCase);
    setExecutionStatus(testCase.execution_status || "pending");
    setNotes(testCase.notes || "");
    setActualHours(testCase.actual_hours || "");
    setActiveTab("results");

    // Load result attachments if they exist
    if (
      testCase.result_attachments &&
      Array.isArray(testCase.result_attachments)
    ) {
      setResultAttachments(testCase.result_attachments);
    } else {
      setResultAttachments([]);
    }

    const testCaseDetails = await testrunService.getTestCaseDetails(
      testRunId,
      testCase.test_run_case_id,
    );
    if (testCaseDetails?.success) {
      setSelectedTestCase(testCaseDetails.data);
    }
    // Load test case attachments
    if (testCase.attachments) {
      const attachments =
        typeof testCase.attachments === "string"
          ? JSON.parse(testCase.attachments)
          : testCase.attachments;
      setTestCaseAttachments(Array.isArray(attachments) ? attachments : []);
    } else {
      setTestCaseAttachments([]);
    }

    // Load test steps for this test case
    if (testCase.test_case_id) {
      setLoadingSteps(true);
      try {
        const response = await teststepsService.getTestSteps(
          testCase.test_case_id,
        );
        let steps = [];
        if (response?.success && response?.data?.test_steps) {
          steps = response.data.test_steps;
        } else if (response?.data?.test_steps) {
          steps = response.data.test_steps;
        } else if (response?.test_steps) {
          steps = response.test_steps;
        } else if (Array.isArray(response?.data)) {
          steps = response.data;
        } else if (Array.isArray(response)) {
          steps = response;
        }
        setTestSteps(steps);
      } catch (error) {
        console.error("Error loading test steps:", error);
        setTestSteps([]);
      } finally {
        setLoadingSteps(false);
      }
    }
  };

  const handleSaveExecution = async () => {
    if (!selectedTestCase) return;

    // Check if test run is completed
    if (testRun?.status === "completed") {
      toast.error("Cannot update execution details for a completed test run");
      return;
    }

    try {
      const updates = {
        execution_status: executionStatus,
        notes: notes || null,
        actual_hours: actualHours ? parseFloat(actualHours) : null,
        result_attachments: resultAttachments,
      };
      await testrunService.updateTestCaseExecution(
        testRunId,
        selectedTestCase.test_run_case_id,
        projectId,
        updates,
      );
      toast.success("Execution details updated successfully");

      // Update local state instead of reloading
      const updatedTestCases = testRun.test_cases.map((tc) =>
        tc.test_run_case_id === selectedTestCase.test_run_case_id
          ? {
              ...tc,
              execution_status: executionStatus,
              notes: notes || null,
              actual_hours: actualHours ? parseFloat(actualHours) : null,
              result_attachments: resultAttachments,
              executed_at:
                executionStatus !== "pending"
                  ? new Date().toISOString()
                  : tc.executed_at,
            }
          : tc,
      );
      setTestRun((prev) => ({
        ...prev,
        test_cases: updatedTestCases,
      }));

      // Update selected test case
      const updatedCase = updatedTestCases.find(
        (tc) => tc.test_run_case_id === selectedTestCase.test_run_case_id,
      );
      if (updatedCase) {
        setSelectedTestCase(updatedCase);
      }

      // Update subgroups
      await groupTestCasesBySection(updatedTestCases, true);
    } catch (error) {
      console.error("Error updating execution:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to update execution";
      if (error.response?.data?.code === "TEST_RUN_COMPLETED") {
        toast.error("Cannot update execution details for a completed test run");
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const getNextTestCase = (currentTestCase, updatedTestCases = null) => {
    // Use updated test cases if provided, otherwise use current testRun.test_cases
    const testCasesToUse = updatedTestCases || testRun?.test_cases;
    if (!testCasesToUse || testCasesToUse.length === 0) return null;

    // Flatten all test cases from all subgroups if available
    let allTestCases = [];
    if (subgroups.length > 0) {
      subgroups.forEach((subgroup) => {
        subgroup.test_cases.forEach((tc) => {
          // Use updated version if available
          const updated = updatedTestCases?.find(
            (utc) => utc.test_run_case_id === tc.test_run_case_id,
          );
          allTestCases.push(updated || tc);
        });
      });
    }

    // If no subgroups or empty, use testCasesToUse directly
    const testCasesList =
      allTestCases.length > 0 ? allTestCases : testCasesToUse;

    // Find current test case index
    const currentIndex = testCasesList.findIndex(
      (tc) => tc.test_run_case_id === currentTestCase.test_run_case_id,
    );

    if (currentIndex === -1) return null;

    // Get next test case
    const nextIndex = currentIndex + 1;
    if (nextIndex < testCasesList.length) {
      return testCasesList[nextIndex];
    }

    return null; // No next test case
  };

  const handleSubmitResultAndNext = async () => {
    if (!selectedTestCase) return;

    // Check if test run is completed
    if (testRun?.status === "completed") {
      toast.error("Cannot update execution details for a completed test run");
      return;
    }

    try {
      // Save current result
      const updates = {
        execution_status: executionStatus,
        notes: notes || null,
        actual_hours: actualHours ? parseFloat(actualHours) : null,
        result_attachments: resultAttachments,
      };
      await testrunService.updateTestCaseExecution(
        testRunId,
        selectedTestCase.test_run_case_id,
        projectId,
        updates,
      );

      // Update local state
      const updatedTestCases = testRun.test_cases.map((tc) =>
        tc.test_run_case_id === selectedTestCase.test_run_case_id
          ? {
              ...tc,
              execution_status: executionStatus,
              notes: notes || null,
              actual_hours: actualHours ? parseFloat(actualHours) : null,
              result_attachments: resultAttachments,
              executed_at:
                executionStatus !== "pending"
                  ? new Date().toISOString()
                  : tc.executed_at,
            }
          : tc,
      );
      setTestRun((prev) => ({
        ...prev,
        test_cases: updatedTestCases,
      }));

      // Update subgroups first
      await groupTestCasesBySection(updatedTestCases);

      // Find and navigate to next test case using updated test cases
      const nextTestCase = getNextTestCase(selectedTestCase, updatedTestCases);
      if (nextTestCase) {
        // Use the updated version of the next test case
        const updatedNextTestCase =
          updatedTestCases.find(
            (tc) => tc.test_run_case_id === nextTestCase.test_run_case_id,
          ) || nextTestCase;
        await handleViewResults(updatedNextTestCase);
        toast.success("Result saved and moved to next test case");
      } else {
        toast.success("Result saved. This is the last test case.");
        // Update the current selected test case to reflect saved changes
        const updatedCurrent = updatedTestCases.find(
          (tc) => tc.test_run_case_id === selectedTestCase.test_run_case_id,
        );
        if (updatedCurrent) {
          setSelectedTestCase(updatedCurrent);
        }
      }
    } catch (error) {
      console.error("Error submitting result:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to submit result";
      if (error.response?.data?.code === "TEST_RUN_COMPLETED") {
        toast.error("Cannot update execution details for a completed test run");
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Validate file size (50 MB max)
    const maxSize = 50 * 1024 * 1024;
    const invalidFiles = files.filter((file) => file.size > maxSize);
    if (invalidFiles.length > 0) {
      toast.error(`Some files exceed 50 MB limit. Max file size: 50 MB`);
      return;
    }

    // Validate total files (10 max)
    if (resultAttachments.length + files.length > 10) {
      toast.error("Maximum 10 files allowed");
      return;
    }

    try {
      setUploadingFiles(true);
      const clientId = getStoredClientId() || user?.client_id;

      if (!clientId) {
        toast.error("Client ID is required for file uploads");
        setUploadingFiles(false);
        return;
      }

      const uploadedUrls = await s3UploadService.uploadFiles(
        files,
        "test-run-results",
        clientId,
        projectId,
      );

      const newAttachments = uploadedUrls.map((url, index) => ({
        url,
        fileName: files[index].name,
        type: files[index].type,
        size: files[index].size,
        uploadedAt: new Date().toISOString(),
      }));

      setResultAttachments((prev) => [...prev, ...newAttachments]);
      toast.success(`${files.length} file(s) uploaded successfully`);
      e.target.value = "";
    } catch (error) {
      console.error("Error uploading files:", error);
      toast.error(error.message || "Failed to upload files");
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleRemoveAttachment = (index) => {
    setResultAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (type) => {
    if (type?.startsWith("image/")) return <ImageIcon />;
    if (type === "application/pdf") return <PdfIcon />;
    return <FileIcon />;
  };

  const isTestRunCompleted = testRun?.status === "completed";

  const handleSelectCase = (testRunCaseId) => {
    setSelectedCases((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(testRunCaseId)) {
        newSet.delete(testRunCaseId);
      } else {
        newSet.add(testRunCaseId);
      }
      return newSet;
    });
  };

  const handleBulkStatusUpdate = async () => {
    if (selectedCases.size === 0) {
      toast.error("Please select at least one test case");
      return;
    }

    // Check if test run is completed
    if (testRun?.status === "completed") {
      toast.error("Cannot update status for a completed test run");
      return;
    }

    try {
      await testrunService.bulkUpdateTestCaseExecution(
        testRunId,
        Array.from(selectedCases),
        projectId,
        { execution_status: bulkStatus },
      );
      toast.success(
        `Updated ${selectedCases.size} test case(s) to ${bulkStatus}`,
      );

      // Update local state instead of reloading
      const updatedTestCases = testRun.test_cases.map((tc) =>
        selectedCases.has(tc.test_run_case_id)
          ? {
              ...tc,
              execution_status: bulkStatus,
              executed_at: new Date().toISOString(),
            }
          : tc,
      );
      setTestRun((prev) => ({
        ...prev,
        test_cases: updatedTestCases,
      }));
      // Update subgroups
      await groupTestCasesBySection(updatedTestCases, true);

      setSelectedCases(new Set());
      setBulkStatus("pending");
    } catch (error) {
      console.error("Error bulk updating status:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to update status";
      if (error.response?.data?.code === "TEST_RUN_COMPLETED") {
        toast.error("Cannot update status for a completed test run");
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const handleTestRunStatusChange = async (newStatus) => {
    if (
      !window.confirm(
        `Are you sure you want to ${newStatus === "completed" ? "complete" : "suspend"} this test run?`,
      )
    ) {
      return;
    }
    try {
      await testrunService.updateTestRun(testRunId, projectId, {
        status: newStatus,
      });
      toast.success(
        `Test run ${newStatus === "completed" ? "completed" : "suspended"} successfully`,
      );
      loadTestRun();
    } catch (error) {
      console.error("Error updating test run status:", error);
      toast.error(
        error.response?.data?.message || "Failed to update test run status",
      );
    }
  };

  const getStatusIcon = (status) => {
    const iconClass = "w-4 h-4";
    switch (status) {
      case "passed":
        return <CheckCircleIcon className={iconClass} />;
      case "failed":
        return <CancelIcon className={iconClass} />;
      case "blocked":
        return <BlockIcon className={iconClass} />;
      case "skipped":
        return <SkipNextIcon className={iconClass} />;
      default:
        return null;
    }
  };

  const getStatusCounts = () => {
    if (!testRun?.test_cases) return {};
    const counts = {
      passed: 0,
      failed: 0,
      blocked: 0,
      skipped: 0,
      untested: 0,
    };
    testRun.test_cases.forEach((tc) => {
      const status = tc.execution_status || "untested";
      if (status === "pending") {
        counts.untested = (counts.untested || 0) + 1;
      } else {
        counts[status] = (counts[status] || 0) + 1;
      }
    });
    return counts;
  };

  const getStatusChartData = () => {
    const counts = getStatusCounts();
    return [
      { name: "Passed", value: counts.passed, color: "#10b981" }, // green
      { name: "Skipped", value: counts.skipped, color: "#f59e0b" }, // yellow/orange
      { name: "Untested", value: counts.untested, color: "#9ca3af" }, // gray
      { name: "Fail", value: counts.failed, color: "#ef4444" }, // red
      { name: "Blocked", value: counts.blocked, color: "#1f2937" }, // dark gray
    ].filter((item) => item.value > 0);
  };

  if (!testRunId || !projectId) {
    return (
      <div className="p-6">
        <h2 className="text-xl font-semibold text-red-600">
          Test Run ID and Project ID are required
        </h2>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <PageSkeleton />
      </div>
    );
  }

  if (!testRun) {
    return (
      <div className="p-6">
        <h2 className="text-xl font-semibold text-red-600">
          Test run not found
        </h2>
      </div>
    );
  }

  const totalCases = testRun.test_cases?.length || 0;
  const statusChartData = getStatusChartData();

  // Get test run creator name (if available)
  const getCreatedByName = () => {
    // This would come from the API response
    return testRun.created_by_name || "Unknown";
  };

  return (
    <div className="p-6 bg-white">
      {/* Breadcrumbs */}
      <nav className="mb-4 flex items-center space-x-2 text-sm">
        <button
          onClick={() => navigate("/projects")}
          className="text-text-secondary hover:text-text-primary"
        >
          Projects
        </button>
        <span className="text-text-tertiary">/</span>
        <button
          onClick={() =>
            navigate(
              `/test-runs?project_id=${projectId}&project_name=${projectName}`,
            )
          }
          className="text-text-secondary hover:text-text-primary"
        >
          Test Runs
        </button>
        <span className="text-text-tertiary">/</span>
        <span className="text-text-primary">{testRun.name}</span>
      </nav>

      {/* Header */}
      <div className="flex justify-between items-center mb-6 gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() =>
                navigate(
                  `/test-runs?project_id=${projectId}&project_name=${projectName}`,
                )
              }
            >
              <ArrowBackIcon className="w-5 h-5" />
            </Button>
            <h1 className="text-3xl font-bold text-text-primary">
              Test Runs & Results
            </h1>
          </div>
        </div>
        <div className="flex gap-2">
          {testRun.status === "active" && (
            <button
              onClick={() => handleTestRunStatusChange("completed")}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-full button-dark-brown transition-colors whitespace-nowrap flex-shrink-0"
            >
              <Lock className="w-4 h-4 flex-shrink-0" />
              <span>Close Run</span>
            </button>
          )}
          <button className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-full button-red transition-colors whitespace-nowrap flex-shrink-0">
            <Download className="w-4 h-4 flex-shrink-0" />
            <span>Export</span>
          </button>
          <button
            onClick={() => {
              navigate(`/settings?tab=variables&section=customization`);
            }}
            className="flex items-center font-medium border-none bg-[#F9F7F5FF] hover:bg-[#EEE7E3FF] transition-colors"
            style={{
              height: "38px",
              padding: "0 16px",
              borderRadius: "18px",
              gap: "8px",
              fontSize: "14px",
              fontWeight: 500,
              color: "#A17561FF",
            }}
          >
            <Settings className="w-4 h-4" />
            <span>Global Variables</span>
          </button>
          <Button
            variant="destructive"
            className="rounded-full"
            onClick={() => setShowPushDefectModal(true)}
            disabled={!clientId || !projectId}
          >
            <Bug className="w-4 h-4 mr-2" />
            Push New Defect
          </Button>
          <Button
            onClick={() =>
              navigate(
                `/test-runs/new?project_id=${projectId}&project_name=${encodeURIComponent(projectName)}&test_run_id=${testRunId}&mode=edit`,
              )
            }
            className="rounded-full"
            variant="outline"
          >
            <EditIcon className="w-4 h-4 mr-2" />
            Edit
          </Button>
        </div>
      </div>

      {/* Test Run Summary Card: row — pie + legends | run details */}
      <Card className="mb-3 border-purple-200 bg-[#EEE7E3FF] p-3">
        <div className="flex flex-col gap-3 md:flex-row md:items-stretch md:gap-4">
          <div className="flex w-full min-w-0 flex-shrink-0 flex-col md:min-w-[17rem] md:max-w-none md:flex-[1.15] lg:min-w-[19rem]">
            <h3 className="mb-1.5 w-full text-sm font-semibold sm:sr-only">
              Test Case Status Distribution
            </h3>
            {statusChartData.length > 0 ? (
              <div className="flex w-full min-w-0 flex-col items-stretch gap-5 sm:flex-row sm:items-center sm:gap-8 md:gap-10">
                <div
                  className="relative mx-auto h-36 w-36 shrink-0 rounded-full sm:mx-0 sm:h-44 sm:w-44 md:h-48 md:w-48"
                  style={{
                    boxShadow:
                      "0 8px 28px rgba(44, 32, 24, 0.12), 0 3px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.85)",
                  }}
                >
                  <div
                    className="absolute inset-0 rounded-full bg-gradient-to-br from-white/90 via-stone-50/50 to-stone-200/40 ring-1 ring-stone-300/40"
                    aria-hidden
                  />
                  <div className="relative h-full w-full p-1">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <defs>
                          <filter
                            id="status-pie-soft-shadow"
                            x="-25%"
                            y="-25%"
                            width="150%"
                            height="150%"
                          >
                            <feDropShadow
                              dx="0"
                              dy="2"
                              stdDeviation="2.5"
                              floodColor="#1c1917"
                              floodOpacity="0.16"
                            />
                          </filter>
                          {statusChartData.map((entry, index) => (
                            <linearGradient
                              key={`grad-${index}`}
                              id={`status-slice-${index}`}
                              x1="0"
                              y1="0"
                              x2="1"
                              y2="1"
                            >
                              <stop
                                offset="0%"
                                stopColor={lightenHex(entry.color, 0.3)}
                              />
                              <stop offset="100%" stopColor={entry.color} />
                            </linearGradient>
                          ))}
                        </defs>
                        <Pie
                          data={statusChartData}
                          cx="50%"
                          cy="50%"
                          dataKey="value"
                          nameKey="name"
                          innerRadius="52%"
                          outerRadius="86%"
                          paddingAngle={2.2}
                          cornerRadius={5}
                          stroke="rgba(255,255,255,0.88)"
                          strokeWidth={2}
                          labelLine={false}
                          isAnimationActive
                          animationDuration={700}
                          animationEasing="ease-out"
                          filter="url(#status-pie-soft-shadow)"
                        >
                          {statusChartData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={`url(#status-slice-${index})`}
                              className="drop-shadow-sm"
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          cursor={{ fill: "transparent" }}
                          content={(tipProps) => (
                            <TestRunStatusPieTooltip {...tipProps} />
                          )}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="pointer-events-none absolute inset-0 z-[1] flex flex-col items-center justify-center text-center">
                    <span className="text-xl font-bold tabular-nums leading-none tracking-tight text-stone-800 drop-shadow-sm md:text-2xl">
                      {totalCases}
                    </span>
                    <span className="mt-1 text-[0.6rem] font-semibold uppercase tracking-[0.1em] text-stone-500">
                      total cases
                    </span>
                  </div>
                </div>
                <div className="min-w-0 flex-1 sm:pl-5 md:pl-7">
                  <h3 className="mb-2 hidden text-sm font-semibold sm:block sm:mb-3">
                    Test Case Status Distribution
                  </h3>
                  <div className="grid grid-cols-1 gap-x-5 gap-y-2 sm:grid-cols-2 sm:gap-x-5 sm:gap-y-2">
                    {statusChartData.map((entry, index) => (
                      <div
                        key={index}
                        className="flex min-w-0 items-center gap-2"
                      >
                        <div
                          className="h-3 w-3 shrink-0 rounded-full"
                          style={{ backgroundColor: entry.color }}
                        />
                        <span className="text-xs leading-snug sm:text-sm">
                          <span className="font-semibold">{entry.value}</span>{" "}
                          {entry.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-xs text-text-secondary sm:text-sm">
                No test cases to display
              </p>
            )}
          </div>

          <div className="min-h-0 min-w-0 flex-1 border-t border-stone-300/50 pt-3 md:border-l md:border-t-0 md:pl-4 md:pt-0">
            <div className="mb-1.5 flex flex-wrap items-center gap-2">
              <h2 className="text-base font-bold text-text-primary md:text-lg">
                {testRun.name}
              </h2>
              <Badge
                variant={testRun.status === "active" ? "default" : "secondary"}
                className={
                  testRun.status === "active"
                    ? "bg-blue-100 text-blue-800"
                    : testRun.status === "completed"
                      ? "bg-green-100 text-green-800"
                      : "bg-gray-100 text-gray-800"
                }
              >
                {testRun.status === "archive" ? "Archive" : testRun.status}
              </Badge>
            </div>
            {testRun.description && (
              <p className="mb-1.5 text-xs text-text-secondary sm:text-sm">
                {testRun.description}
              </p>
            )}
            <div className="grid grid-cols-2 gap-x-2 gap-y-1.5 text-xs sm:text-sm md:gap-x-2 md:gap-y-2 lg:grid-cols-3 xl:grid-cols-4">
              <div>
                <p className="text-text-secondary">Project</p>
                <p className="font-medium break-words text-text-primary">
                  {testRun.project_name}
                </p>
              </div>
              <div>
                <p className="text-text-secondary">Created By</p>
                <p className="font-medium text-text-primary">
                  {testRun.created_by_name || getCreatedByName()}
                </p>
              </div>
              <div>
                <p className="text-text-secondary">Assigned To</p>
                <p className="font-medium text-text-primary">
                  {testRun.assigned_to_name || "Unassigned"}
                </p>
              </div>
              <div>
                <p className="text-text-secondary">Created On</p>
                <p className="font-medium text-text-primary">
                  {testRun.created_at ? formatDate(testRun.created_at) : "-"}
                </p>
              </div>
              <div>
                <p className="text-text-secondary">Total Test Cases</p>
                <p className="font-medium text-text-primary">{totalCases}</p>
              </div>
              {testRun.references && (
                <div>
                  <p className="text-text-secondary">Reference</p>
                  <div className="flex flex-wrap gap-1">
                    {testRun.references.split(",").map((ref, idx) => (
                      <a
                        key={idx}
                        href={`#${ref.trim()}`}
                        className="text-sm text-blue-600 hover:underline"
                      >
                        {ref.trim()}
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {testRun.test_plan_ids &&
                Array.isArray(testRun.test_plan_ids) &&
                testRun.test_plan_ids.length > 0 && (
                  <div>
                    <p className="text-text-secondary">Test Plan</p>
                    <div className="flex flex-wrap gap-1">
                      {testRun.test_plan_ids.map((planId, idx) => (
                        <a
                          key={idx}
                          href={`#${planId}`}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          {planId.startsWith("TP-") ? planId : `TP-${planId}`}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      </Card>

      {/* Bulk Action Toolbar */}
      {selectedCases.size > 0 && !isTestRunCompleted && (
        <Card className="p-4 mb-4 bg-[#EEE7E3FF]">
          <div className="flex items-center gap-3 flex-wrap">
            <p className="text-sm font-medium text-text-primary">
              {selectedCases.size} test case(s) selected
            </p>
            <div className="flex-1 min-w-[150px]">
              <Label htmlFor="bulk-status">Bulk Status</Label>
              <Select
                value={bulkStatus}
                onValueChange={(value) => setBulkStatus(value)}
              >
                <SelectTrigger id="bulk-status" className="mt-1">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="passed">Passed</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                  <SelectItem value="skipped">Skipped</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              size="sm"
              onClick={handleBulkStatusUpdate}
              className="bg-primary-600 hover:bg-primary-700"
            >
              Apply to Selected
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const targets = Array.from(selectedCases)
                  .map((runCaseId) => {
                    const tc = testRun?.test_cases?.find(
                      (c) => c.test_run_case_id === runCaseId,
                    );
                    return tc
                      ? { entity_type: "test_case", entity_id: tc.test_case_id }
                      : null;
                  })
                  .filter(Boolean);
                if (targets.length > 0) {
                  setLinkDefectTargets(targets);
                  setShowLinkDefectModal(true);
                }
              }}
              disabled={!clientId || !projectId}
            >
              <Bug className="w-4 h-4 mr-2" />
              Link Defect
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedCases(new Set())}
            >
              Clear Selection
            </Button>
          </div>
        </Card>
      )}

      {/* Main: page scrolls above; table region is height capped with internal scroll */}
      <div
        className={`grid w-full min-w-0 items-stretch gap-6 ${
          selectedTestCase ? "min-h-0 lg:grid-cols-3" : ""
        }`}
      >
        <div
          className={
            selectedTestCase
              ? "min-h-0 min-w-0 lg:col-span-2 lg:h-full"
              : "col-span-full min-w-0"
          }
        >
          <div
            className={`flex w-full min-w-0 items-stretch overflow-x-hidden rounded-lg border border-gray-200 bg-bg-primary shadow max-h-[calc(100vh-10.5rem)] ${
              selectedTestCase
                ? "h-[min(72vh,42rem)] max-lg:min-h-[22rem] lg:min-h-0 lg:h-full"
                : "h-[min(72vh,42rem)] min-h-[22rem]"
            }`}
          >
            {subgroups && subgroups.length > 0 && (
              <aside className="sticky top-4 z-30 flex h-full min-h-0 w-[240px] flex-shrink-0 flex-col self-stretch border-r border-gray-200 bg-[#E3E1DFFF]">
                <div
                  className="flex flex-shrink-0 items-center justify-between"
                  style={{
                    height: "48px",
                    borderBottom: "1px solid #E5E7EB",
                    padding: "0 12px",
                  }}
                >
                  <select
                    value={sidebarMode}
                    onChange={(e) => {
                      setSidebarMode(e.target.value);
                      if (e.target.value === "all") {
                        setCurrentTreeSection(null);
                      }
                    }}
                    className="cursor-pointer border-none bg-transparent text-sm font-medium text-gray-700 outline-none"
                    style={{ fontSize: "14px", fontWeight: 500 }}
                  >
                    <option value="subgroups">Subgroups</option>
                    <option value="all">All</option>
                  </select>
                </div>
                <div className="min-h-0 flex-1 space-y-0.5 overflow-y-auto overflow-x-hidden py-2">
                  {sectionLoading && (
                    <div className="px-3 text-xs text-gray-500">Loading…</div>
                  )}
                  {sidebarMode === "subgroups" && subgroups.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setCurrentTreeSection(null)}
                      className={`flex w-full min-h-9 items-center gap-1 rounded-lg px-3 py-1.5 text-left text-sm transition-colors ${
                        currentTreeSection == null
                          ? "bg-white/90 font-medium text-text-primary shadow-sm ring-1 ring-stone-300/50 [&_svg]:text-primary-1000"
                          : "text-primary-1000 text-text-primary hover:bg-[#f5f5f7] [&_svg]:text-primary-1000"
                      }`}
                    >
                      <span className="w-4 shrink-0" aria-hidden />
                      <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center">
                        <FolderOpen className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1 truncate">
                        All subgroups
                      </span>
                    </button>
                  )}
                  {subgroups.map((sg) => {
                    const id = sg.section_id;
                    const isSubgroupMode = sidebarMode === "subgroups";
                    const isRowHighlighted =
                      isSubgroupMode &&
                      (String(currentTreeSection) === String(id) ||
                        (currentTreeSection == null && subgroups.length === 1));

                    return (
                      <button
                        key={String(id)}
                        type="button"
                        onClick={() => {
                          if (sidebarMode === "subgroups") {
                            setCurrentTreeSection(id);
                          } else {
                            setScrollToSectionId(id);
                          }
                        }}
                        className={`flex w-full min-h-9 items-center gap-1 rounded-lg px-3 py-1.5 text-left text-sm transition-colors ${
                          isRowHighlighted
                            ? "bg-white/90 font-medium text-text-primary shadow-sm ring-1 ring-stone-300/50 [&_svg]:text-primary-1000"
                            : "text-primary-1000 text-text-primary hover:bg-[#f5f5f7] [&_svg]:text-primary-1000"
                        }`}
                      >
                        <span className="w-4 shrink-0" aria-hidden />
                        <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center">
                          <Folder className="h-4 w-4" />
                        </span>
                        <span className="min-w-0 flex-1 truncate">
                          {sg.section_name}
                        </span>
                        <span
                          className={
                            isRowHighlighted
                              ? "shrink-0 text-xs text-text-primary/80"
                              : "shrink-0 text-xs text-text-primary"
                          }
                        >
                          ({sg.total})
                        </span>
                      </button>
                    );
                  })}
                </div>
              </aside>
            )}

            <div className="flex min-h-0 min-w-0 flex-1 flex-col">
              <div
                className="flex flex-shrink-0 items-center justify-between border-b border-gray-200"
                style={{ height: "48px", padding: "0 12px" }}
              >
                <h2
                  className="m-0"
                  style={{
                    fontSize: "16px",
                    fontWeight: 600,
                    color: "#111827",
                  }}
                >
                  Test Cases
                </h2>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 w-4 -translate-y-1/2 transform text-gray-400" />
                    <Input
                      value={tableSearch}
                      onChange={(e) => setTableSearch(e.target.value)}
                      placeholder="Search by Title..."
                      className="h-9 w-[200px] pl-8"
                    />
                  </div>
                  <Button variant="outline" size="icon" type="button">
                    <Filter className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="icon" type="button">
                    <Columns className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-0 pb-3 pt-0">
                <div>
                  {subgroups.length === 0 && testRun?.test_cases?.length > 0 ? (
                    // Fallback: Show all test cases in a single group if grouping failed
                    <div className="border-b last:border-b-0">
                      <div className="p-4 bg-gray-50 hover:bg-gray-100 cursor-pointer flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1">
                          <span className="font-semibold text-text-primary">
                            All Test Cases ({filteredFallbackTestCases.length}{" "}
                            Test{" "}
                            {filteredFallbackTestCases.length === 1
                              ? "Case"
                              : "Cases"}
                            )
                          </span>
                          <div className="flex-1 max-w-[200px]">
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-blue-600 h-2 rounded-full transition-all"
                                style={{
                                  width:
                                    filteredFallbackTestCases.length > 0
                                      ? `${Math.round(
                                          (filteredFallbackTestCases.filter(
                                            (tc) =>
                                              tc.execution_status &&
                                              tc.execution_status !==
                                                "pending" &&
                                              tc.execution_status !==
                                                "untested",
                                          ).length /
                                            filteredFallbackTestCases.length) *
                                            100,
                                        )}%`
                                      : "0%",
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="mb-4 bg-white border border-gray-200 rounded-md rounded-tr-none rounded-tl-none overflow-x-auto w-full">
                        <table className="section-based-test-case-table table-auto w-full min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              {!isTestRunCompleted && (
                                <th className="w-12 px-4 py-3 column-header">
                                  <input
                                    type="checkbox"
                                    checked={
                                      filteredFallbackTestCases.length > 0 &&
                                      filteredFallbackTestCases.every((tc) =>
                                        selectedCases.has(tc.test_run_case_id),
                                      )
                                    }
                                    ref={(input) => {
                                      if (
                                        input &&
                                        filteredFallbackTestCases.length > 0
                                      ) {
                                        const ids =
                                          filteredFallbackTestCases.map(
                                            (tc) => tc.test_run_case_id,
                                          );
                                        const some = ids.some((id) =>
                                          selectedCases.has(id),
                                        );
                                        const every = ids.every((id) =>
                                          selectedCases.has(id),
                                        );
                                        input.indeterminate = some && !every;
                                      }
                                    }}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedCases(
                                          new Set(
                                            filteredFallbackTestCases.map(
                                              (tc) => tc.test_run_case_id,
                                            ),
                                          ),
                                        );
                                      } else {
                                        setSelectedCases(new Set());
                                      }
                                    }}
                                    className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                  />
                                </th>
                              )}
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider column-header">
                                ID
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider column-header whitespace-nowrap">
                                Title
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider column-header">
                                Priority
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider column-header">
                                Labels
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide column-header">
                                Assign To
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider column-header">
                                Status
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider column-header"></th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {filteredFallbackTestCases.map((testCase) => (
                              <tr
                                key={testCase.test_run_case_id}
                                className={`transition-colors test-case-row-slide-in group ${
                                  selectedCases.has(testCase.test_run_case_id)
                                    ? "bg-indigo-50"
                                    : "hover:bg-gray-50"
                                }`}
                              >
                                {!isTestRunCompleted && (
                                  <td className="px-4 py-3">
                                    <input
                                      type="checkbox"
                                      checked={selectedCases.has(
                                        testCase.test_run_case_id,
                                      )}
                                      onChange={() =>
                                        handleSelectCase(
                                          testCase.test_run_case_id,
                                        )
                                      }
                                      onClick={(e) => e.stopPropagation()}
                                      className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                    />
                                  </td>
                                )}
                                <td className="px-4 py-3 text-sm font-medium td-default underline">
                                  <button
                                    type="button"
                                    onClick={() => handleViewResults(testCase)}
                                    className="font-medium text-left td-default underline"
                                    title={testCase.test_case_id}
                                  >
                                    {testCase.test_case_id?.substring(0, 8) ||
                                      "N/A"}
                                  </button>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <button
                                    type="button"
                                    onClick={() => handleViewResults(testCase)}
                                    className="font-medium text-left td-default underline"
                                  >
                                    {testCase.title}
                                  </button>
                                </td>
                                <td className="px-4 py-3">
                                  <span className="text-base font-medium capitalize text-gray-500">
                                    {testCase.priority || "medium"}
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex flex-wrap gap-1">
                                    {testCase.tags &&
                                    Array.isArray(testCase.tags) &&
                                    testCase.tags.length > 0 ? (
                                      <>
                                        {testCase.tags
                                          .slice(0, 2)
                                          .map((tag, idx) => (
                                            <TestRunLabelBadge
                                              key={idx}
                                              label={tag}
                                            />
                                          ))}
                                        {testCase.tags.length > 2 && (
                                          <span className="text-xs text-gray-500">
                                            +{testCase.tags.length - 2}
                                          </span>
                                        )}
                                      </>
                                    ) : (
                                      <span className="text-xs text-gray-400">
                                        -
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setShowAssignToModal(true);
                                      setSelectedTestCase(testCase);
                                    }}
                                    className="font-medium text-left td-default underline"
                                  >
                                    {testCase.assigned_to_name || "Unassigned"}
                                  </button>
                                </td>
                                <td className="px-2 py-3 w-20">
                                  <ExecutionStatusDropdown
                                    status={testCase.execution_status}
                                    testRunCaseId={testCase.test_run_case_id}
                                    onStatusChange={handleStatusChange}
                                    disabled={isTestRunCompleted}
                                  />
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="w-4 h-4 text-gray-400"
                                    title="Open this test case"
                                    onClick={() => handleViewResults(testCase)}
                                  >
                                    <ChevronRight className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : !testRun?.test_cases?.length ? (
                    <div className="p-8 text-center text-text-secondary">
                      No test cases in this test run
                    </div>
                  ) : displaySubgroups.length === 0 ? (
                    <div className="p-8 text-center text-text-secondary">
                      No test cases match the current section filter or search.
                    </div>
                  ) : (
                    displaySubgroups.map((subgroup) => {
                      const isExpanded = expandedSections.has(
                        subgroup.section_id,
                      );
                      const isSidebarSelectedSubgroup =
                        sidebarMode === "subgroups" &&
                        (String(currentTreeSection) ===
                          String(subgroup.section_id) ||
                          (currentTreeSection == null &&
                            subgroups.length === 1));
                      const toggleSection = () => {
                        setExpandedSections((prev) => {
                          const newSet = new Set(prev);
                          if (newSet.has(subgroup.section_id)) {
                            newSet.delete(subgroup.section_id);
                          } else {
                            newSet.add(subgroup.section_id);
                          }
                          return newSet;
                        });
                      };

                      return (
                        <div
                          key={subgroup.section_id}
                          className="border-b last:border-b-0"
                        >
                          {/* Section Header — selected subgroup: beige + left accent, no indigo */}
                          <div
                            id={`testrun-section-${subgroup.section_id}`}
                            className={
                              isSidebarSelectedSubgroup
                                ? "p-4 pl-3 cursor-pointer flex items-center justify-between border-l-[3px] border-l-[#7c6a5a] bg-[#E3D7D1FF] font-medium text-text-primary"
                                : "p-4 cursor-pointer flex items-center justify-between bg-[#E3D7D1FF]"
                            }
                            onClick={toggleSection}
                          >
                            <div className="flex items-center gap-3 flex-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-text-primary hover:bg-white/30"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleSection();
                                }}
                              >
                                {isExpanded ? (
                                  <ChevronDown className="w-4 h-4" />
                                ) : (
                                  <ChevronRight className="w-4 h-4" />
                                )}
                              </Button>
                              <span className="font-semibold text-text-primary">
                                {subgroup.section_name} ({subgroup.total} Test{" "}
                                {subgroup.total === 1 ? "Case" : "Cases"})
                              </span>
                              <div className="flex-1 max-w-[200px]">
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div
                                    className="bg-[#A17561FF] h-2 rounded-full transition-all"
                                    style={{ width: `${subgroup.progress}%` }}
                                  />
                                </div>
                              </div>
                              <span className="text-sm text-[#1C1918FF]">
                                {subgroup.progress}%
                              </span>
                            </div>
                          </div>

                          {/* Section Test Cases Table */}
                          {isExpanded && (
                            <div className="mb-4 bg-white border border-gray-200 rounded-md rounded-tr-none rounded-tl-none overflow-x-auto w-full">
                              <table className="section-based-test-case-table table-auto w-full min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    {!isTestRunCompleted && (
                                      <th className="w-12 px-4 py-3 column-header">
                                        <input
                                          type="checkbox"
                                          checked={
                                            subgroup.test_cases.length > 0 &&
                                            subgroup.test_cases.every((tc) =>
                                              selectedCases.has(
                                                tc.test_run_case_id,
                                              ),
                                            )
                                          }
                                          ref={(input) => {
                                            if (
                                              input &&
                                              subgroup.test_cases.length > 0
                                            ) {
                                              const ids =
                                                subgroup.test_cases.map(
                                                  (tc) => tc.test_run_case_id,
                                                );
                                              const some = ids.some((id) =>
                                                selectedCases.has(id),
                                              );
                                              const every = ids.every((id) =>
                                                selectedCases.has(id),
                                              );
                                              input.indeterminate =
                                                some && !every;
                                            }
                                          }}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              const sectionCaseIds = new Set(
                                                subgroup.test_cases.map(
                                                  (tc) => tc.test_run_case_id,
                                                ),
                                              );
                                              setSelectedCases(
                                                (prev) =>
                                                  new Set([
                                                    ...prev,
                                                    ...sectionCaseIds,
                                                  ]),
                                              );
                                            } else {
                                              const sectionCaseIds = new Set(
                                                subgroup.test_cases.map(
                                                  (tc) => tc.test_run_case_id,
                                                ),
                                              );
                                              setSelectedCases((prev) => {
                                                const newSet = new Set(prev);
                                                sectionCaseIds.forEach((id) =>
                                                  newSet.delete(id),
                                                );
                                                return newSet;
                                              });
                                            }
                                          }}
                                          className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                        />
                                      </th>
                                    )}
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider column-header">
                                      ID
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider column-header whitespace-nowrap">
                                      Title
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider column-header">
                                      Priority
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider column-header">
                                      Labels
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide column-header">
                                      Assign To
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider column-header">
                                      Status
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider column-header"></th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {subgroup.test_cases.map((testCase) => (
                                    <tr
                                      key={testCase.test_run_case_id}
                                      className={`transition-colors test-case-row-slide-in group ${
                                        selectedCases.has(
                                          testCase.test_run_case_id,
                                        )
                                          ? "bg-indigo-50"
                                          : "hover:bg-gray-50"
                                      }`}
                                    >
                                      {!isTestRunCompleted && (
                                        <td className="px-4 py-3">
                                          <input
                                            type="checkbox"
                                            checked={selectedCases.has(
                                              testCase.test_run_case_id,
                                            )}
                                            onChange={() =>
                                              handleSelectCase(
                                                testCase.test_run_case_id,
                                              )
                                            }
                                            onClick={(e) => e.stopPropagation()}
                                            className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                          />
                                        </td>
                                      )}
                                      <td className="px-4 py-3 text-sm font-medium td-default underline">
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleViewResults(testCase)
                                          }
                                          className="font-medium text-left td-default underline"
                                          title={testCase.test_case_id}
                                        >
                                          {testCase.test_case_id?.substring(
                                            0,
                                            8,
                                          ) || "N/A"}
                                        </button>
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap">
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleViewResults(testCase)
                                          }
                                          className="font-medium text-left td-default underline"
                                        >
                                          {testCase.title}
                                        </button>
                                      </td>
                                      <td className="px-4 py-3">
                                        <span className="text-base font-medium capitalize text-gray-500">
                                          {testCase.priority || "medium"}
                                        </span>
                                      </td>
                                      <td className="px-4 py-3">
                                        <div className="flex flex-wrap gap-1">
                                          {testCase.tags &&
                                          Array.isArray(testCase.tags) &&
                                          testCase.tags.length > 0 ? (
                                            <>
                                              {testCase.tags
                                                .slice(0, 2)
                                                .map((tag, idx) => (
                                                  <TestRunLabelBadge
                                                    key={idx}
                                                    label={tag}
                                                  />
                                                ))}
                                              {testCase.tags.length > 2 && (
                                                <span className="text-xs text-gray-500">
                                                  +{testCase.tags.length - 2}
                                                </span>
                                              )}
                                            </>
                                          ) : (
                                            <span className="text-xs text-gray-400">
                                              -
                                            </span>
                                          )}
                                        </div>
                                      </td>
                                      <td className="px-4 py-3">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setSelectedTestCase(testCase);
                                            setShowAssignToModal(true);
                                          }}
                                          className="font-medium text-left td-default underline"
                                        >
                                          {testCase.assigned_to_name ||
                                            "Unassigned"}
                                        </button>
                                      </td>
                                      <td className="px-4 py-3">
                                        <ExecutionStatusDropdown
                                          status={testCase.execution_status}
                                          testRunCaseId={
                                            testCase.test_run_case_id
                                          }
                                          onStatusChange={handleStatusChange}
                                          disabled={isTestRunCompleted}
                                        />
                                      </td>
                                      <td>
                                        <button
                                          type="button"
                                          className="w-4 h-4 text-gray-400"
                                          title="Open this test case"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleViewResults(testCase);
                                          }}
                                        >
                                          <ChevronRight className="w-4 h-4" />
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Test Case Details Panel */}
        {selectedTestCase && (
          <div className="min-h-0 self-stretch lg:col-span-1">
            <Card className="flex h-[calc(100vh-250px)] min-h-0 flex-col">
              {/* Test Case Header */}
              <div className="p-4 border-b flex items-center justify-between bg-gray-50">
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex w-full min-w-0 items-center gap-2">
                    <Badge
                      variant={"outline"}
                      className="shrink-0 p-2 text-sm font-semibold text-white bg-[#9333EAFF]"
                    >
                      {selectedTestCase.test_case_id}
                    </Badge>
                    <h3 className="min-w-0 flex-1 text-lg font-semibold text-gray-900 truncate">
                      {selectedTestCase.title}
                    </h3>
                    <Badge
                      className={`shrink-0 flex rounded-lg items-center gap-1 ${triggerClassFor(
                        selectedTestCase.execution_status || "untested",
                      )}`}
                    >
                      {getStatusIcon(
                        selectedTestCase.execution_status || "untested",
                      )}
                      <span className="text-xs">
                        {selectedTestCase.execution_status || "Untested"}
                      </span>
                    </Badge>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setSelectedTestCase(null);
                    setTestSteps([]);
                    setResultAttachments([]);
                    setTestCaseAttachments([]);
                  }}
                  className="h-8 w-8"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              {/* Test Case Details Content */}
              <div className="min-h-0 flex-1 overflow-y-auto p-4">
                {/* Test Case Attributes */}
                <div className="mb-6 space-y-3 bg-[#EEE7E3FF] p-4 rounded-lg">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-600">Type:</span>
                      <span className="ml-2 text-gray-900">
                        {selectedTestCase.type_of_test_case || "Other"}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Priority:</span>
                      <Badge
                        variant={
                          selectedTestCase.priority === "high" ||
                          selectedTestCase.priority === "critical"
                            ? "destructive"
                            : "secondary"
                        }
                        className="ml-2 text-xs"
                      >
                        {selectedTestCase.priority || "Medium"}
                      </Badge>
                    </div>
                    <div>
                      <span className="text-gray-600">Assigned To:</span>
                      <span className="ml-2 text-gray-900">
                        {selectedTestCase.assigned_to_name ||
                          selectedTestCase.owner_name ||
                          "None"}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Estimate:</span>
                      <span className="ml-2 text-gray-900">
                        {selectedTestCase.estimated_time
                          ? `${selectedTestCase.estimated_time}h`
                          : "None"}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">References:</span>
                      <span className="ml-2 text-gray-900">
                        {selectedTestCase.references || "None"}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Automation Type:</span>
                      <span className="ml-2 text-gray-900">
                        {selectedTestCase.automation_status || "None"}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Is Automated:</span>
                      <span className="ml-2 text-gray-900">
                        {selectedTestCase.automation_status === "automated"
                          ? "Yes"
                          : "No"}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Approver:</span>
                      <span className="ml-2 text-gray-900">
                        {selectedTestCase.reviewer_name || "None"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Preconditions */}
                {selectedTestCase.preconditions && (
                  <div className="mb-6">
                    <div className="mb-2 flex items-center gap-3">
                      <h4 className="shrink-0 text-md font-semibold text-gray-900">
                        Preconditions
                      </h4>
                      <div
                        className="h-px min-w-0 flex-1 bg-[#C5C1BFFF]"
                        aria-hidden
                      />
                    </div>
                    <div className="space-y-1">
                      {selectedTestCase.preconditions
                        .split("\n")
                        .filter((p) => p.trim())
                        .map((precondition, idx) => (
                          <div
                            key={idx}
                            className="flex items-start gap-2 text-sm text-gray-700"
                          >
                            <span className="font-semibold text-gray-600">
                              {idx + 1}.
                            </span>
                            <span>{precondition.trim()}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Steps */}
                {testSteps.length > 0 && (
                  <div className="mb-6">
                    <div className="mb-3 flex items-center gap-3">
                      <h4 className="shrink-0 text-md font-semibold text-gray-900">
                        Steps
                      </h4>
                      <div
                        className="h-px min-w-0 flex-1 bg-[#C5C1BFFF]"
                        aria-hidden
                      />
                    </div>
                    <div className="space-y-4">
                      {testSteps.map((step, index) => {
                        const stepContent = String(
                          step.action ||
                            step.description ||
                            step.step ||
                            "No action specified",
                        );
                        const expectedResult = step.expected_result || "";
                        const hasHtml =
                          stepContent.includes("<") &&
                          stepContent.includes(">");

                        return (
                          <div
                            key={step.step_id || index}
                            className="border-l-2 border-[#C5C1BFFF] pl-3"
                          >
                            <div className="flex items-start gap-2 mb-1">
                              <span className="font-semibold text-sm text-gray-600">
                                {step.step_number || index + 1}.
                              </span>
                              <div className="flex-1">
                                {hasHtml ? (
                                  <div
                                    className="text-sm text-gray-900 [&_img]:max-w-full [&_img]:max-h-[200px] [&_img]:rounded"
                                    dangerouslySetInnerHTML={{
                                      __html: stepContent,
                                    }}
                                  />
                                ) : (
                                  <p className="text-sm text-gray-900">
                                    {stepContent}
                                  </p>
                                )}
                                {expectedResult && (
                                  <div className="mt-2">
                                    <p className="text-xs font-medium text-gray-600 mb-1">
                                      Expected Result:
                                    </p>
                                    <p className="text-xs text-gray-700">
                                      {expectedResult}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Attachments */}
                {testCaseAttachments.length > 0 && (
                  <div className="mb-6">
                    <div className="mb-3 flex items-center gap-3">
                      <h4 className="shrink-0 text-md font-semibold text-gray-900">
                        Attachments
                      </h4>
                      <div
                        className="h-px min-w-0 flex-1 bg-[#C5C1BFFF]"
                        aria-hidden
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {testCaseAttachments.map((attachment, index) => {
                        const url =
                          typeof attachment === "string"
                            ? attachment
                            : attachment.url;
                        const fileName =
                          typeof attachment === "string"
                            ? `Attachment ${index + 1}`
                            : attachment.fileName || attachment.name || "File";
                        const type =
                          typeof attachment === "string"
                            ? "unknown"
                            : attachment.type || "unknown";
                        const isImage = type.startsWith("image/");
                        const fileSize = attachment.size
                          ? `${(attachment.size / 1024).toFixed(0)} KB`
                          : "";

                        return (
                          <div
                            key={index}
                            className="border border-gray-200 rounded p-2 overflow-hidden"
                          >
                            {isImage ? (
                              <img
                                src={url}
                                alt={fileName}
                                className="w-full h-24 object-cover rounded mb-1 cursor-pointer"
                                onClick={() => window.open(url, "_blank")}
                              />
                            ) : (
                              <div className="w-full h-24 bg-gray-100 rounded flex items-center justify-center mb-1">
                                <FileIcon className="w-8 h-8 text-gray-400" />
                              </div>
                            )}
                            <p
                              className="text-xs text-gray-600 truncate w-full"
                              title={fileName}
                              style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "100%",
                              }}
                            >
                              {fileName}
                            </p>
                            {fileSize && (
                              <p className="text-xs text-gray-500">
                                {fileSize}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Tabs for Results/History/Defects */}
                <Tabs
                  value={activeTab}
                  onValueChange={setActiveTab}
                  className="w-full"
                >
                  <TabsList className="grid h-auto w-full grid-cols-3 rounded-none bg-transparent p-0">
                    <TabsTrigger
                      value="results"
                      className="rounded-none border-b-2 border-b-transparent py-2.5 data-[state=active]:border-b-[#A17561FF] data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                    >
                      Results
                    </TabsTrigger>
                    <TabsTrigger
                      value="history"
                      className="rounded-none border-b-2 border-b-transparent py-2.5 data-[state=active]:border-b-[#A17561FF] data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                    >
                      History
                    </TabsTrigger>
                    <TabsTrigger
                      value="defects"
                      className="rounded-none border-b-2 border-b-transparent py-2.5 data-[state=active]:border-b-[#A17561FF] data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                    >
                      Defects
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="results" className="mt-4">
                    <div className="space-y-4">
                      <div>
                        <Badge variant="outline" className="mb-2">
                          {selectedTestCase.execution_status || "Untested"}
                        </Badge>
                        <div className="text-sm space-y-1 text-gray-600">
                          <p>
                            Assigned To:{" "}
                            {selectedTestCase.assigned_to_name ||
                              selectedTestCase.owner_name ||
                              "None"}
                          </p>
                          <p>
                            Added By:{" "}
                            {selectedTestCase.executed_by_name ||
                              user?.full_name ||
                              "Unknown"}
                          </p>
                          <p>
                            Added At:{" "}
                            {selectedTestCase.executed_at
                              ? formatDateTime(selectedTestCase.executed_at)
                              : "N/A"}
                          </p>
                        </div>
                        {selectedTestCase.notes && (
                          <p className="mt-2 text-sm text-gray-700">
                            {selectedTestCase.notes}
                          </p>
                        )}
                        {!selectedTestCase.notes && (
                          <p className="mt-2 text-sm text-gray-500 italic">
                            This test was marked as '
                            {selectedTestCase.execution_status || "Untested"}'.
                          </p>
                        )}
                      </div>
                      {resultAttachments.length > 0 && (
                        <div>
                          <h5 className="text-sm font-semibold text-gray-900 mb-2">
                            Result Attachments
                          </h5>
                          <div className="space-y-2">
                            {resultAttachments.map((attachment, index) => {
                              const url = attachment.url || attachment;
                              const fileName =
                                attachment.fileName ||
                                attachment.name ||
                                `File ${index + 1}`;
                              const type = attachment.type || "unknown";
                              const isImage = type.startsWith("image/");
                              return (
                                <div
                                  key={index}
                                  className="flex items-center gap-2 p-2 border border-gray-200 rounded"
                                >
                                  {isImage ? (
                                    <img
                                      src={url}
                                      alt={fileName}
                                      className="w-16 h-16 object-cover rounded cursor-pointer"
                                      onClick={() => window.open(url, "_blank")}
                                    />
                                  ) : (
                                    <FileIcon className="w-8 h-8 text-gray-400" />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm text-gray-900 truncate">
                                      {fileName}
                                    </p>
                                    {attachment.size && (
                                      <p className="text-xs text-gray-500">
                                        {(attachment.size / 1024).toFixed(0)} KB
                                      </p>
                                    )}
                                  </div>
                                  {!isTestRunCompleted && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6 text-red-600"
                                      onClick={() => {
                                        setResultAttachments((prev) =>
                                          prev.filter((_, i) => i !== index),
                                        );
                                      }}
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="history" className="mt-4">
                    {false ? (
                      <div className="text-sm text-gray-600">
                        <p>History functionality coming soon</p>
                      </div>
                    ) : (
                      <NoContentComponent title="No history found" />
                    )}
                  </TabsContent>

                  <TabsContent value="defects" className="mt-4">
                    <div className="text-sm text-gray-600 space-y-3">
                      <NoContentComponent title="No defects found" />
                    </div>
                  </TabsContent>
                </Tabs>
              </div>

              {/* Action Buttons Footer */}
              <div className="p-4 border-t bg-gray-50">
                {!isTestRunCompleted && (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddResultModal(true);
                          setAddingResultCase(selectedTestCase);
                        }}
                        className="flex min-h-8 flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium button-brown transition-colors"
                      >
                        <Plus className="h-4 w-4 shrink-0" />
                        <span>Add Result</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowAssignToModal(true)}
                        className="button-info-soft flex-1"
                      >
                        Assign To
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={async () => {
                          await handleStatusChange(
                            selectedTestCase.test_run_case_id,
                            "passed",
                          );
                          setSelectedTestCase(null);
                        }}
                        className="flex min-h-8 flex-1 items-center justify-center gap-2 rounded-md border border-[#A17561FF] px-3 py-2 text-sm font-medium button-brown-inverted transition-colors"
                      >
                        Pass & Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* Add Result Modal */}
      <AddResultModal
        open={showAddResultModal}
        onClose={() => {
          setShowAddResultModal(false);
          setAddingResultCase(null);
        }}
        testCase={addingResultCase}
        testRunId={testRunId}
        projectId={projectId}
        onSave={async (resultData) => {
          if (!addingResultCase) return;
          await testrunService.updateTestCaseExecution(
            testRunId,
            addingResultCase.test_run_case_id,
            projectId,
            resultData,
          );
          loadTestRun();
        }}
        isTestRunCompleted={isTestRunCompleted}
      />

      <AssignToModal
        open={showAssignToModal}
        onClose={() => setShowAssignToModal(false)}
        testCase={selectedTestCase}
        testRunId={testRunId}
        projectId={projectId}
        onSuccess={handleAssignSuccess}
        isTestRunCompleted={isTestRunCompleted}
        assignees={assignees}
      />

      <PushNewDefectModal
        open={showPushDefectModal}
        onClose={() => setShowPushDefectModal(false)}
        clientId={clientId}
        projectId={projectId}
        onSuccess={loadTestRun}
      />

      <LinkDefectModal
        open={showLinkDefectModal}
        onClose={() => {
          setShowLinkDefectModal(false);
          setLinkDefectTargets(null);
        }}
        clientId={clientId}
        projectId={projectId}
        linkTargets={linkDefectTargets}
        entityType={linkDefectTargets ? null : "test_case"}
        entityId={
          linkDefectTargets?.[0]?.entity_id || selectedTestCase?.test_case_id
        }
        onSuccess={loadTestRun}
        onCreateNew={() => {
          setShowLinkDefectModal(false);
          setShowPushDefectModal(true);
        }}
      />
    </div>
  );
};

export default TestRunExecutionPage;
